import { useEffect, useState } from "react";
import { Mail, Phone, MessageCircle, Loader2, Archive, Check, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  new:      { label: "Neu",       cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  read:     { label: "Gelesen",   cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  replied:  { label: "Beantwortet", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  archived: { label: "Archiviert",  cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

const MAIL_STATUS_META = {
  sent: { label: "Mail versendet", cls: "text-emerald-400" },
  pending_provider: { label: "Mail wartet (kein Provider)", cls: "text-amber-400" },
  pending: { label: "Mail in Warteschlange", cls: "text-blue-400" },
  sending: { label: "Mail wird gesendet…", cls: "text-blue-400" },
  failed: { label: "Mail-Fehler", cls: "text-red-400" },
};

export default function AdminContact() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/contact", { params });
      setItems(data || []);
    } catch (e) {
      toast.error("Konnte Kontaktanfragen nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);   // eslint-disable-line

  const counts = items.reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {});

  const update = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/contact/${id}`, updates);
      setItems((prev) => prev.map((x) => (x.id === id ? data : x)));
      setSelected(data);
      toast.success("Aktualisiert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-6 text-white" data-testid="admin-contact-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Kontakt</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Kontaktanfragen</h1>
          <p className="text-sm text-zinc-500 mt-1.5">Nachrichten aus dem Kontaktformular · Persistent in DB · E-Mail-Status sichtbar.</p>
        </div>
        <div className="flex gap-2 text-xs flex-wrap">
          {["all", "new", "read", "replied", "archived"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 h-9 rounded-full transition-all ${
                filter === s
                  ? "bg-[#DC1928] text-white"
                  : "bg-[#141417] border border-white/10 text-zinc-400 hover:text-white"
              }`}
              data-testid={`contact-filter-${s}`}
            >
              {s === "all" ? "Alle" : STATUS_META[s]?.label}
              {counts[s] > 0 && <span className="ml-1.5 opacity-70">·{counts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-20 rounded-2xl border border-dashed border-white/10 bg-[#0F0F11] text-center" data-testid="contact-empty">
          <Mail className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <div className="font-display text-base text-white">Keine Nachrichten</div>
          <p className="text-xs text-zinc-500 mt-1">Sobald ein Kunde das Kontaktformular ausfüllt, erscheint es hier.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contact-grid">
          {items.map((it) => (
            <ContactCard key={it.id} item={it} onClick={() => setSelected(it)} />
          ))}
        </div>
      )}

      {selected && (
        <ContactDetail
          item={selected}
          onClose={() => setSelected(null)}
          onUpdate={(u) => update(selected.id, u)}
        />
      )}
    </div>
  );
}

function ContactCard({ item, onClick }) {
  const st = STATUS_META[item.status] || STATUS_META.new;
  const ms = MAIL_STATUS_META[item.email_notification_status] || null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-2xl bg-[#141417] border border-white/8 hover:border-white/20 transition-colors"
      data-testid={`contact-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-white text-sm truncate">{item.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{item.email}</div>
        </div>
        <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
      </div>
      <div className="text-xs text-zinc-400 font-medium mb-1 line-clamp-1">{item.subject || "—"}</div>
      <p className="text-xs text-zinc-500 line-clamp-2 leading-relaxed">{item.message}</p>
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px]">
        <span className="text-zinc-600">{(item.created_at || "").replace("T", " ").slice(0, 16)}</span>
        {ms && <span className={ms.cls}>{ms.label}</span>}
      </div>
    </button>
  );
}

function ContactDetail({ item, onClose, onUpdate }) {
  const [note, setNote] = useState(item.admin_note || "");
  useEffect(() => { setNote(item.admin_note || ""); }, [item.id, item.admin_note]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto" data-testid="contact-detail">
      <div className="w-full max-w-2xl bg-[#0F0F11] border border-white/10 rounded-3xl p-6 md:p-8 text-white">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Kontaktanfrage</div>
            <div className="font-display text-2xl font-bold mt-1">{item.name}</div>
            <div className="text-sm text-zinc-500 mt-0.5">{(item.created_at || "").replace("T", " ").slice(0, 16)}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="contact-close">✕</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <a href={`mailto:${item.email}`} className="flex items-center gap-2 p-3 rounded-xl bg-[#141417] border border-white/8 hover:border-white/20">
            <Mail className="w-4 h-4 text-[#FF4F5E]" />
            <span className="text-sm text-white truncate">{item.email}</span>
          </a>
          {item.phone && (
            <a href={`tel:${item.phone}`} className="flex items-center gap-2 p-3 rounded-xl bg-[#141417] border border-white/8 hover:border-white/20">
              <Phone className="w-4 h-4 text-[#FF4F5E]" />
              <span className="text-sm text-white truncate">{item.phone}</span>
            </a>
          )}
        </div>

        {item.subject && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Betreff</div>
            <div className="text-sm text-white mt-1">{item.subject}</div>
          </div>
        )}

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Nachricht</div>
          <div className="mt-2 p-4 rounded-xl bg-[#141417] border border-white/8 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{item.message}</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-xs mb-5">
          <div className="p-3 rounded-xl bg-[#141417] border border-white/8">
            <div className="text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Betreiber-Mail</div>
            <MailStatusBadge status={item.email_notification_status} messageId={item.email_notification_id} />
          </div>
          <div className="p-3 rounded-xl bg-[#141417] border border-white/8">
            <div className="text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Kunden-Bestätigung</div>
            <MailStatusBadge status={item.customer_confirmation_status} messageId={item.customer_confirmation_id} />
          </div>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Interne Notiz</div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-2 bg-[#141417] border-white/10 text-white" data-testid="contact-note" />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onUpdate({ admin_note: note })} className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full" data-testid="contact-save">
            <Send className="w-4 h-4 mr-1.5" /> Notiz speichern
          </Button>
          <Button onClick={() => onUpdate({ status: "read" })} variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full" data-testid="contact-mark-read">
            <Check className="w-4 h-4 mr-1.5" /> Gelesen
          </Button>
          <Button onClick={() => onUpdate({ status: "replied" })} variant="outline" className="bg-transparent border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 rounded-full" data-testid="contact-mark-replied">
            <Check className="w-4 h-4 mr-1.5" /> Beantwortet
          </Button>
          <Button onClick={() => onUpdate({ status: "archived" })} variant="outline" className="bg-transparent border-white/15 text-zinc-400 hover:bg-white/5 hover:text-white rounded-full" data-testid="contact-mark-archived">
            <Archive className="w-4 h-4 mr-1.5" /> Archivieren
          </Button>
        </div>
      </div>
    </div>
  );
}

function MailStatusBadge({ status, messageId }) {
  const [retrying, setRetrying] = useState(false);
  const meta = MAIL_STATUS_META[status] || { label: "—", cls: "text-zinc-500" };
  const retry = async () => {
    if (!messageId) return;
    setRetrying(true);
    try {
      const { data } = await api.post(`/admin/emails/${messageId}/retry`);
      if (data?.ok) toast.success("Mail nachgesendet");
      else if (data?.status === "pending_provider") toast.info("Kein Provider konfiguriert — Mail bleibt in Warteschlange");
      else toast.error(data?.error || "Retry fehlgeschlagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry fehlgeschlagen");
    } finally {
      setRetrying(false);
    }
  };
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className={`text-xs font-semibold ${meta.cls}`}>{meta.label}</div>
      {messageId && (status === "failed" || status === "pending_provider") && (
        <button
          onClick={retry}
          disabled={retrying}
          className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 disabled:opacity-50"
        >
          {retrying ? "…" : "Nachsenden"}
        </button>
      )}
    </div>
  );
}
